import React, { useEffect, useRef, useState } from 'react';
import {
    computeObjectFitContainLayout,
    getVideoElementFromRef,
    videoElementRectToFrameRegion,
} from '@/utils/video-player';
import { useVideoPlayerContext } from './VideoPlayerProvider';

const LOUPE_SIZE_PX = 300;
const LOUPE_SIZE_EXPANDED_PX = 440;
const MIN_MAGNIFICATION = 1.5;
const MAX_MAGNIFICATION = 8;
const MAGNIFICATION_STEP = 0.5;
// Scroll distance (in normalized pixels) that maps to one magnification step.
const WHEEL_NOTCH_PX = 100;
const WHEEL_LINE_HEIGHT_PX = 16;
const WHEEL_PAGE_HEIGHT_PX = 800;

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

/**
 * A magnifying-glass loupe overlay. While zoom mode is active it follows the
 * pointer over the video and renders the region beneath it, magnified by the
 * context zoom factor, into an in-place canvas. The canvas is redrawn every
 * animation frame so it stays live while the video plays.
 */
export const VideoMagnifier: React.FC = () => {
    const {
        zoomed,
        zoomLocked,
        zoomFactor,
        activeMediaRef,
        containerRef,
        expanded,
    } = useVideoPlayerContext();
    const loupeSize = expanded ? LOUPE_SIZE_EXPANDED_PX : LOUPE_SIZE_PX;
    const loupeRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const cursorRef = useRef<{ clientX: number; clientY: number } | null>(null);
    const lastCursorRef = useRef<{ clientX: number; clientY: number } | null>(
        null,
    );
    const labelRef = useRef<HTMLSpanElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const magnificationRef = useRef(zoomFactor);
    const wheelAccumRef = useRef(0);
    const frozenRef = useRef(zoomLocked);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        frozenRef.current = zoomLocked;
        if (zoomLocked && !cursorRef.current) {
            cursorRef.current = lastCursorRef.current;
        }
    }, [zoomLocked]);

    useEffect(() => {
        if (!zoomed) {
            setVisible(false);
            cursorRef.current = null;
            lastCursorRef.current = null;
            return;
        }

        const container = containerRef.current;
        if (!container) return;

        magnificationRef.current = clamp(
            zoomFactor,
            MIN_MAGNIFICATION,
            MAX_MAGNIFICATION,
        );
        wheelAccumRef.current = 0;

        const handlePointerMove = (event: PointerEvent) => {
            if (frozenRef.current) return;
            cursorRef.current = {
                clientX: event.clientX,
                clientY: event.clientY,
            };
            lastCursorRef.current = cursorRef.current;
        };
        const handlePointerLeave = () => {
            if (frozenRef.current) return;
            cursorRef.current = null;
            setVisible(false);
        };
        const handleWheel = (event: WheelEvent) => {
            // Scroll up zooms in, scroll down zooms out. Prevent the default
            // page/seek scroll while adjusting the loupe magnification.
            event.preventDefault();

            // A single physical notch can emit several wheel events (and the
            // delta magnitude varies by device and deltaMode). Normalize to
            // pixels and accumulate so one notch maps to exactly one step,
            // regardless of how many events it produced.
            const normalizedDelta =
                event.deltaMode === 1
                    ? event.deltaY * WHEEL_LINE_HEIGHT_PX
                    : event.deltaMode === 2
                      ? event.deltaY * WHEEL_PAGE_HEIGHT_PX
                      : event.deltaY;
            wheelAccumRef.current += normalizedDelta;

            while (wheelAccumRef.current <= -WHEEL_NOTCH_PX) {
                wheelAccumRef.current += WHEEL_NOTCH_PX;
                magnificationRef.current = clamp(
                    magnificationRef.current + MAGNIFICATION_STEP,
                    MIN_MAGNIFICATION,
                    MAX_MAGNIFICATION,
                );
            }
            while (wheelAccumRef.current >= WHEEL_NOTCH_PX) {
                wheelAccumRef.current -= WHEEL_NOTCH_PX;
                magnificationRef.current = clamp(
                    magnificationRef.current - MAGNIFICATION_STEP,
                    MIN_MAGNIFICATION,
                    MAX_MAGNIFICATION,
                );
            }
        };

        container.addEventListener('pointermove', handlePointerMove);
        container.addEventListener('pointerleave', handlePointerLeave);
        container.addEventListener('wheel', handleWheel, { passive: false });

        const draw = () => {
            rafRef.current = requestAnimationFrame(draw);

            const cursor = cursorRef.current;
            const canvas = canvasRef.current;
            const loupe = loupeRef.current;
            const videoElement = getVideoElementFromRef(
                activeMediaRef,
            ) as HTMLVideoElement | null;

            if (!cursor || !canvas || !loupe || !videoElement) {
                return;
            }

            const videoWidth = videoElement.videoWidth;
            const videoHeight = videoElement.videoHeight;
            if (!videoWidth || !videoHeight) {
                setVisible(false);
                return;
            }

            const videoRect = videoElement.getBoundingClientRect();
            const { renderedWidth, renderedHeight, offsetX, offsetY } =
                computeObjectFitContainLayout(videoElement);

            // Pointer position within the rendered (letterbox-corrected) video.
            const renderedX = cursor.clientX - videoRect.left - offsetX;
            const renderedY = cursor.clientY - videoRect.top - offsetY;

            const insideVideo =
                renderedX >= 0 &&
                renderedX <= renderedWidth &&
                renderedY >= 0 &&
                renderedY <= renderedHeight;

            if (!insideVideo) {
                setVisible(false);
                return;
            }
            setVisible(true);

            const magnification = magnificationRef.current;
            if (labelRef.current) {
                labelRef.current.textContent = `${magnification.toFixed(1)}x`;
            }

            // The loupe shows loupeSize magnified by `magnification`, so the
            // sampled span in rendered (CSS) pixels is loupeSize /
            // magnification. Build that span as a cursor-centered rectangle in
            // the element's local CSS coordinates. Unlike the cropper, the
            // sample is NOT position-clamped: the cursor point must stay at the
            // centre of the (round) loupe so edges and corners are reachable.
            // The rectangle is allowed to extend past the video; only its
            // visible intersection is sampled and drawn to the matching sub-area
            // of the canvas, leaving off-video regions blank.
            const sampleCssWidth = loupeSize / magnification;
            const sampleCssHeight = loupeSize / magnification;
            const rectLeft = offsetX + renderedX - sampleCssWidth / 2;
            const rectTop = offsetY + renderedY - sampleCssHeight / 2;

            // Clip the cursor-centred rectangle to the visible video area.
            const videoRight = offsetX + renderedWidth;
            const videoBottom = offsetY + renderedHeight;
            const visibleLeft = clamp(rectLeft, offsetX, videoRight);
            const visibleTop = clamp(rectTop, offsetY, videoBottom);
            const visibleRight = clamp(
                rectLeft + sampleCssWidth,
                offsetX,
                videoRight,
            );
            const visibleBottom = clamp(
                rectTop + sampleCssHeight,
                offsetY,
                videoBottom,
            );
            const visibleWidth = visibleRight - visibleLeft;
            const visibleHeight = visibleBottom - visibleTop;
            if (visibleWidth <= 0 || visibleHeight <= 0) return;

            const region = videoElementRectToFrameRegion(videoElement, {
                left: visibleLeft,
                top: visibleTop,
                width: visibleWidth,
                height: visibleHeight,
            });
            if (!region) return;

            const context = canvas.getContext('2d');
            if (!context) return;

            // Map the visible sample back onto the canvas at the same relative
            // offset it occupies within the full (unclamped) rectangle, so the
            // cursor point lands at the loupe centre regardless of edges.
            const destScaleX = loupeSize / sampleCssWidth;
            const destScaleY = loupeSize / sampleCssHeight;
            const destX = (visibleLeft - rectLeft) * destScaleX;
            const destY = (visibleTop - rectTop) * destScaleY;
            const destWidth = visibleWidth * destScaleX;
            const destHeight = visibleHeight * destScaleY;

            try {
                context.clearRect(0, 0, loupeSize, loupeSize);
                context.drawImage(
                    videoElement,
                    region.sourceX,
                    region.sourceY,
                    region.sourceWidth,
                    region.sourceHeight,
                    destX,
                    destY,
                    destWidth,
                    destHeight,
                );
            } catch {
                // drawImage can throw transiently before the first frame is
                // decoded; ignore and retry on the next animation frame.
            }

            // Position the loupe centered on the pointer, within the container.
            const containerRect = container.getBoundingClientRect();
            const loupeLeft = clamp(
                cursor.clientX - containerRect.left - loupeSize / 2,
                0,
                containerRect.width - loupeSize,
            );
            const loupeTop = clamp(
                cursor.clientY - containerRect.top - loupeSize / 2,
                0,
                containerRect.height - loupeSize,
            );
            loupe.style.left = `${loupeLeft}px`;
            loupe.style.top = `${loupeTop}px`;
        };

        rafRef.current = requestAnimationFrame(draw);

        return () => {
            container.removeEventListener('pointermove', handlePointerMove);
            container.removeEventListener('pointerleave', handlePointerLeave);
            container.removeEventListener('wheel', handleWheel);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [zoomed, zoomFactor, activeMediaRef, containerRef, loupeSize]);

    if (!zoomed) return null;

    return (
        <div
            ref={loupeRef}
            className="pointer-events-none absolute z-40 overflow-hidden rounded-full border-[3px] border-white-100 shadow-lg"
            style={{
                width: loupeSize,
                height: loupeSize,
                visibility: visible ? 'visible' : 'hidden',
            }}
        >
            <canvas
                ref={canvasRef}
                width={loupeSize}
                height={loupeSize}
                className="h-full w-full bg-black-100"
            />
            <span
                ref={labelRef}
                className="absolute bottom-2 right-2 rounded bg-black-100/70 px-2 py-1 text-base font-semibold text-white-100"
            />
        </div>
    );
};
